import React from 'react';
import { 
  TrendingUp, 
  Droplets, 
  IndianRupee, 
  Wallet,
  AlertCircle
} from 'lucide-react';
import { 
  AreaChart, Area, 
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { dashboardData, tanks } from '../mockData';

const Dashboard = () => {
  return (
    <div className="space-y-6 pb-12">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard Overview</h1>
          <p className="text-slate-500 text-sm mt-1">Live snapshot of today's operations and inventory.</p>
        </div>
        <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 shadow-sm">
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* Tank Inventory Section */}
      <h2 className="text-lg font-bold text-slate-800 mt-8 mb-2">Underground Tanks Inventory</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tanks.map(tank => {
          const fillPercentage = (tank.currentStock / tank.capacity) * 100;
          const isLow = fillPercentage < 25;
          const barColor = tank.type === 'Petrol' ? 'bg-orange-500' : tank.type === 'Diesel' ? 'bg-blue-500' : 'bg-emerald-500';
          const bgColor = tank.type === 'Petrol' ? 'bg-orange-50' : tank.type === 'Diesel' ? 'bg-blue-50' : 'bg-emerald-50';
          
          return (
            <div key={tank.id} className="glass-panel p-5 relative overflow-hidden flex flex-col justify-between h-40 group hover:border-slate-300 transition-colors">
              <div className="flex justify-between items-start z-10">
                <div>
                  <h3 className="font-bold text-slate-800">{tank.name}</h3>
                  <p className="text-xs font-semibold text-slate-500">{tank.type}</p>
                </div>
                {isLow && (
                  <span className="flex items-center gap-1 text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-100">
                    <AlertCircle className="w-3 h-3" /> LOW
                  </span>
                )}
              </div>

              <div className="z-10 mt-4">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-2xl font-bold text-slate-900">{tank.currentStock.toLocaleString()} L</span>
                  <span className="text-sm font-medium text-slate-500">/ {tank.capacity.toLocaleString()} L</span>
                </div>
                {/* Progress Bar representation */}
                <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden border border-slate-200 shadow-inner">
                  <div 
                    className={`h-full ${barColor} transition-all duration-1000 ease-in-out ${isLow ? 'animate-pulse' : ''}`}
                    style={{ width: `${fillPercentage}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Background decorative element */}
              <div className={`absolute -right-10 -bottom-10 w-32 h-32 rounded-full blur-3xl opacity-40 ${bgColor} pointer-events-none group-hover:opacity-60 transition-opacity`}></div>
            </div>
          );
        })}
      </div>

      <h2 className="text-lg font-bold text-slate-800 mt-8 mb-2">Financial Snapshot</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="glass-panel p-5 flex items-start gap-4">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
            <Droplets className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500">Total Liters Sold</p>
            <p className="text-xl font-bold text-slate-900 mt-1">{dashboardData.summary.totalSalesLiters.toLocaleString()} L</p>
          </div>
        </div>

        <div className="glass-panel p-5 flex items-start gap-4">
          <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
            <IndianRupee className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500">Expected Sales Amount</p>
            <p className="text-xl font-bold text-slate-900 mt-1">₹{dashboardData.summary.totalSalesAmount.toLocaleString()}</p>
          </div>
        </div>

        <div className="glass-panel p-5 flex items-start gap-4">
          <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
            <Wallet className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500">Total Collections</p>
            <p className="text-xl font-bold text-slate-900 mt-1">₹{dashboardData.summary.totalCollections.toLocaleString()}</p>
          </div>
        </div>

        <div className="glass-panel p-5 flex items-start gap-4">
          <div className={`p-3 rounded-xl ${dashboardData.summary.difference < 0 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
            <AlertCircle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500">Net Shortage/Excess</p>
            <p className={`text-xl font-bold mt-1 ${dashboardData.summary.difference < 0 ? 'text-red-600' : 'text-green-600'}`}>
              {dashboardData.summary.difference < 0 ? '-' : '+'}₹{Math.abs(dashboardData.summary.difference).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 glass-panel p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-6">Today's Sales Trend</h2>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dashboardData.salesTrend}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dx={-10} tickFormatter={(value) => `₹${value/1000}k`} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  formatter={(value) => [`₹${value.toLocaleString()}`, 'Sales']}
                />
                <Area type="monotone" dataKey="sales" stroke="#0ea5e9" strokeWidth={3} fillOpacity={1} fill="url(#colorSales)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-panel p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-6">Collection Modes</h2>
          <div className="h-[300px] w-full flex flex-col items-center justify-center relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={dashboardData.collectionsByMode}
                  cx="50%"
                  cy="50%"
                  innerRadius={80}
                  outerRadius={110}
                  paddingAngle={5}
                  dataKey="value"
                  stroke="none"
                >
                  {dashboardData.collectionsByMode.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                />
              </PieChart>
            </ResponsiveContainer>
            
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-slate-400 text-sm font-medium">Total</span>
              <span className="text-xl font-bold text-slate-800">
                ₹{(dashboardData.collectionsByMode.reduce((a, b) => a + b.value, 0) / 100000).toFixed(1)}L
              </span>
            </div>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            {dashboardData.collectionsByMode.map(mode => (
              <div key={mode.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: mode.color }}></div>
                <span className="text-sm font-medium text-slate-600">{mode.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
